// Auto-generated lightweight class for TaskHistory
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { TaskHistoryReadAPI, TaskHistoryWriteAPI, TaskHistoryReadAPISchema, TaskHistoryWriteAPISchema } from '../types/taskhistory';
import { ModelRegistry } from '../../registries/model-registry';
import { AtScope, ContributesTo, CreatedBy, DesignsPackage, HasContext, HasSteps, HasThread, HasThreads } from '../../relationships/relationship';

import { SecurityScope } from '../../shared/api/securityscope';
import { Workspace } from '../../base/api/workspace';
import { Task } from '../../tasks/api/task';
import { User } from '../../base/api/user';
import { ScopedNode } from '../../shared/api/scopednode';
import { Step } from '../../chat/api/step';
import { MBSE_Package } from '../../mbse/api/mbse_package';
import { ThreadTask } from './threadtask';



export interface TaskHistory extends Partial<TaskHistoryReadAPI> {}
export class TaskHistory extends NeoNodeBase<TaskHistoryReadAPI, TaskHistoryWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "TaskHistory";
  static readonly readSchema = TaskHistoryReadAPISchema;
  static readonly writeSchema = TaskHistoryWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    workspace: { target: "Workspace", cardinality: "one", isParent: false, relClass: "HasThreads", direction: "in" },
    task: { target: "Task", cardinality: "one", isParent: true, relClass: "HasThread", direction: "in" },
    created_by: { target: "User", cardinality: "one", isParent: false, relClass: "CreatedBy", direction: "out" },
    context_items: { target: "ScopedNode", cardinality: "many", isParent: false, relClass: "HasContext", direction: "out" },
    steps: { target: "Step", cardinality: "many", isParent: false, relClass: "HasSteps", direction: "out" },
    dev_package: { target: "MBSE_Package", cardinality: "one", isParent: false, relClass: "DesignsPackage", direction: "out" },
    chat_tasks: { target: "ThreadTask", cardinality: "many", isParent: false, relClass: "ContributesTo", direction: "in" },
  };

  protected get modelName(): string {
    return 'TaskHistory';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return TaskHistory.label;
  }


  constructor(data: Partial<TaskHistoryWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: TaskHistoryReadAPI, loadContext?: Record<string, any>): TaskHistory {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { created_by, context_items, steps, security_scope, workspace, task, dev_package, chat_tasks, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = TaskHistoryReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, created_by: created_by, context_items: context_items, steps: steps, security_scope: security_scope, workspace: workspace, task: task, dev_package: dev_package, chat_tasks: chat_tasks };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return TaskHistory.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadOneRelationship(processed, 'created_by', 'User', ModelRegistry);
    loadManyRelationship(processed, 'context_items', 'ScopedNode', ModelRegistry);
    loadManyRelationship(processed, 'steps', 'Step', ModelRegistry);
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'workspace', 'Workspace', ModelRegistry);
    loadOneRelationship(processed, 'task', 'Task', ModelRegistry);
    loadOneRelationship(processed, 'dev_package', 'MBSE_Package', ModelRegistry);
    loadManyRelationship(processed, 'chat_tasks', 'ThreadTask', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new TaskHistory(processed as any);

    return instance;
  }


  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'workspace' - Python ORM style (cached)
  get workspace(): RelationshipManager<this, Workspace> {
    return this.getOrCreateRelationshipManager('workspace', () => {
      return new RelationshipManager(this, 'workspace', 'one', HasThreads as typeof Relationship);
    });
  }

  // Relationship manager for 'task' - Python ORM style (cached)
  get task(): RelationshipManager<this, Task> {
    return this.getOrCreateRelationshipManager('task', () => {
      return new RelationshipManager(this, 'task', 'one', HasThread as typeof Relationship);
    });
  }

  // Relationship manager for 'created_by' - Python ORM style (cached)
  get created_by(): RelationshipManager<this, User> {
    return this.getOrCreateRelationshipManager('created_by', () => {
      return new RelationshipManager(this, 'created_by', 'one', CreatedBy as typeof Relationship);
    });
  }

  // Relationship manager for 'context_items' - Python ORM style (cached)
  get context_items(): RelationshipManager<this, ScopedNode> {
    return this.getOrCreateRelationshipManager('context_items', () => {
      return new RelationshipManager(this, 'context_items', 'many', HasContext as typeof Relationship);
    });
  }

  // Relationship manager for 'steps' - Python ORM style (cached)
  get steps(): RelationshipManager<this, Step> {
    return this.getOrCreateRelationshipManager('steps', () => {
      return new RelationshipManager(this, 'steps', 'many', HasSteps as typeof Relationship);
    });
  }

  // Relationship manager for 'dev_package' - Python ORM style (cached)
  get dev_package(): RelationshipManager<this, MBSE_Package> {
    return this.getOrCreateRelationshipManager('dev_package', () => {
      return new RelationshipManager(this, 'dev_package', 'one', DesignsPackage as typeof Relationship);
    });
  }

  // Relationship manager for 'chat_tasks' - Python ORM style (cached)
  get chat_tasks(): RelationshipManager<this, ThreadTask> {
    return this.getOrCreateRelationshipManager('chat_tasks', () => {
      return new RelationshipManager(this, 'chat_tasks', 'many', ContributesTo as typeof Relationship);
    });
  }

}
